import { Box, Container, Typography, Link as MuiLink, Paper } from '@mui/material';
import { Link } from 'react-router-dom';

const SECTIONS = [
  { id: 'contact-support', title: 'Contact support' },
  { id: 'password-help', title: 'Password help' },
  { id: 'report-issue', title: 'Report an issue' },
  { id: 'browsers-devices', title: 'Browsers and devices' },
  { id: 'account-access', title: 'Account and access' },
  { id: 'more-information', title: 'More information' },
];

export default function Support() {
  return (
    <Box sx={{ py: 4, px: 2, minHeight: '100vh', bgcolor: 'background.default' }}>
      <Container maxWidth="md">
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
          Support
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Get help with Galldris Core
        </Typography>

        <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Contents
          </Typography>
          <Box component="ol" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            {SECTIONS.map(({ id, title }) => (
              <li key={id}>
                <MuiLink href={`#${id}`} underline="hover" color="primary" sx={{ fontSize: '0.8125rem' }}>
                  {title}
                </MuiLink>
              </li>
            ))}
          </Box>
        </Paper>

        <Typography id="contact-support" variant="h6" component="h2" sx={{ mt: 3, mb: 1, scrollMarginTop: 16 }}>
          1. Contact support
        </Typography>
        <Typography variant="body2" paragraph>
          For technical help or questions about Galldris Core, contact your IT department or the Software development team. Use the contact details provided in your internal communications or contract.
        </Typography>

        <Typography id="password-help" variant="h6" component="h2" sx={{ mt: 3, mb: 1, scrollMarginTop: 16 }}>
          2. Password help
        </Typography>
        <Typography variant="body2" paragraph>
          If you have forgotten your password, use the{' '}
          <MuiLink component={Link} to="/login" underline="hover">
            Log in
          </MuiLink>{' '}
          page and click &quot;Forgotten password?&quot; You will receive an email with a link to reset your password. If you do not receive the email, check your spam folder or contact your administrator.
        </Typography>
        <Typography variant="body2" paragraph>
          If you are locked out of your account or need your password reset by an administrator, contact your IT support or project administrator.
        </Typography>

        <Typography id="report-issue" variant="h6" component="h2" sx={{ mt: 3, mb: 1, scrollMarginTop: 16 }}>
          3. Report an issue
        </Typography>
        <Typography variant="body2" paragraph>
          To report a bug, error, or technical problem:
        </Typography>
        <Typography component="ul" variant="body2" sx={{ pl: 2, mb: 2 }}>
          <li>Contact your IT department or the Software development team.</li>
          <li>Describe the issue, the steps to reproduce it, and (if possible) your browser and device.</li>
          <li>Include any error messages you see.</li>
        </Typography>
        <Typography variant="body2" paragraph>
          We aim to respond to support requests as soon as practicable. For urgent outages affecting site operations, use your usual escalation process.
        </Typography>

        <Typography id="browsers-devices" variant="h6" component="h2" sx={{ mt: 3, mb: 1, scrollMarginTop: 16 }}>
          4. Browsers and devices
        </Typography>
        <Typography variant="body2" paragraph>
          Galldris Core works on modern browsers (Chrome, Edge, Firefox, Safari) and is mobile-ready. For the best experience:
        </Typography>
        <Typography component="ul" variant="body2" sx={{ pl: 2, mb: 2 }}>
          <li>Use a supported, up-to-date browser.</li>
          <li>Ensure a stable internet connection where possible; the app supports offline use for some features.</li>
        </Typography>
        <Typography variant="body2" paragraph>
          If you experience problems, try clearing your browser cache, disabling extensions, or using a different browser.
        </Typography>

        <Typography id="account-access" variant="h6" component="h2" sx={{ mt: 3, mb: 1, scrollMarginTop: 16 }}>
          5. Account and access
        </Typography>
        <Typography variant="body2" paragraph>
          If you need a new account, a change to your access level, or help with project or site access, contact your project administrator or the Galldris team managing your project.
        </Typography>
        <Typography variant="body2" paragraph>
          For data subject rights (access to your data, rectification, erasure, or portability), see our{' '}
          <MuiLink component={Link} to="/privacy-policy" underline="hover">
            Privacy Notice
          </MuiLink>
          . When logged in, you can use &quot;Download my data&quot; and &quot;Request account deletion&quot; under Privacy &amp; data in your user menu.
        </Typography>

        <Typography id="more-information" variant="h6" component="h2" sx={{ mt: 3, mb: 1, scrollMarginTop: 16 }}>
          6. More information
        </Typography>
        <Typography variant="body2" paragraph>
          For how we process your personal data, see our{' '}
          <MuiLink component={Link} to="/privacy-policy" underline="hover">
            Privacy Notice
          </MuiLink>
          . For how we use cookies and similar technologies, see our{' '}
          <MuiLink component={Link} to="/cookie-policy" underline="hover">
            Cookie Policy
          </MuiLink>.
        </Typography>

        <Box sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <MuiLink component={Link} to="/login" underline="hover" sx={{ mr: 2 }}>
            Back to login
          </MuiLink>
          <MuiLink component={Link} to="/privacy-policy" underline="hover" sx={{ mr: 2 }}>
            Privacy Notice
          </MuiLink>
          <MuiLink component={Link} to="/cookie-policy" underline="hover">
            Cookie Policy
          </MuiLink>
        </Box>
      </Container>
    </Box>
  );
}
